import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import JuniorCallFlow from './JuniorCallFlow';
import './JuniorCalls.css';

const DRAFT_STORAGE_KEY = 'junior_call_draft';

const getDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {}
};

const JuniorCalls = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const moderatorFromLocation = location.state?.moderator ?? null;

  const [moderator, setModerator] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [initialDraft, setInitialDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const draft = getDraft();
    if (moderatorFromLocation) {
      setModerator(moderatorFromLocation);
      if (draft && draft.moderator?.discord_id === moderatorFromLocation.discord_id) {
        setShowResumePrompt(true);
        setInitialDraft(null);
      }
    } else if (draft?.moderator) {
      setModerator(draft.moderator);
      setShowResumePrompt(true);
      setInitialDraft(null);
    }
  }, [moderatorFromLocation]);

  const handleComplete = useCallback(
    async (score) => {
      if (!moderator?.discord_id || saving) return;
      setSaving(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/junior-calls', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            juniorDiscordId: moderator.discord_id,
            juniorNickname: moderator.nickname || '—',
            score
          })
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          console.error('Ошибка сохранения обзвона:', err);
        }
      } catch (e) {
        console.error('Ошибка сохранения обзвона:', e);
      } finally {
        setSaving(false);
      }
    },
    [moderator, saving]
  );

  const handleClose = useCallback(() => {
    clearDraft();
    navigate('/discordmoderation/statistics-junior', { replace: true });
  }, [navigate]);

  const handleContinue = useCallback(() => {
    const draft = getDraft();
    if (draft && draft.moderator?.discord_id === moderator?.discord_id) {
      setInitialDraft({ step: draft.step, questionIds: draft.questionIds, answers: draft.answers || [] });
    }
    setShowResumePrompt(false);
  }, [moderator]);

  const handleStartNew = useCallback(() => {
    clearDraft();
    setShowResumePrompt(false);
    setInitialDraft(null);
  }, []);

  if (showResumePrompt && moderator) {
    return (
      <div className="junior-calls-container junior-calls-standalone">
        <div className="junior-calls-resume-prompt">
          <p className="junior-calls-resume-prompt-text">
            Есть незавершённый обзвон. Хотите продолжить или начать новый?
          </p>
          <p className="junior-calls-resume-prompt-sub">Модератор: {moderator.nickname}</p>
          <div className="junior-calls-resume-prompt-buttons">
            <button type="button" className="junior-calls-btn junior-calls-btn-resume" onClick={handleContinue}>
              Продолжить
            </button>
            <button type="button" className="junior-calls-btn junior-calls-btn-start-new" onClick={handleStartNew}>
              Начать новый
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (moderator) {
    return (
      <div className="junior-calls-container junior-calls-standalone">
        <JuniorCallFlow
          moderator={moderator}
          initialDraft={initialDraft}
          onClose={handleClose}
          onComplete={handleComplete}
          isModal={false}
        />
        {saving && (
          <div className="junior-calls-saving-overlay">
            <span>Сохранение результата...</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="junior-calls-container">
      <div className="junior-calls-empty-state-page">
        <h2 className="junior-calls-empty-state-title">Обзвон Junior-модераторов</h2>
        <p className="junior-calls-empty-state-text">
          Выберите младшего модератора на странице «Статистика — Junior» и нажмите «Начать обзвон», чтобы перейти сюда.
        </p>
        <button
          type="button"
          className="junior-calls-btn junior-calls-btn-start"
          onClick={() => navigate('/discordmoderation/statistics-junior')}
        >
          Перейти к Статистике Junior
        </button>
      </div>
    </div>
  );
};

export default JuniorCalls;
